<template>
  <div v-if="eventTasks && eventTasks.length > 0" class="events">
    <ul class="events-list">
      <li v-for="event in displayedEvents" :key="event.event.id">
        <div class="event" @click="openEvent(event.event.id, $event)">
          <div class="event-title">
            <p>
              {{ event.event.title }}
            </p>
            <NotesIcon
              v-if="event.event.notes && event.event.notes.blocks.length > 0"
            />
          </div>
          <p class="event-time">
            {{ formatRelativeDate(event.event.start) }}
          </p>
          <ul v-if="event.tasks.length > 0" class="uncompleted-tasks">
            <li v-for="task in event.tasks" :key="task.id">
              <BaseCheckbox
                :title="task.title"
                :is-checked="!!task.completed"
                :is-ignored="!!task.ignored"
                :is-disabled="task.integrationStatus?.status === 'broken'"
                :checkbox-tooltip="
                  taskCheckboxTooltip(!!task.completed, !!task.ignored)
                "
                @click="() => toggleCheckbox(task)"
              />
            </li>
          </ul>
        </div>
      </li>
      <li v-if="hasMoreEvents">
        <BaseButton
          kind="auxiliary"
          type="secondary"
          text="Load more"
          @click="loadMore"
        />
      </li>
    </ul>
    <div class="events-side">
      <ActivityIcon class="icon" />
      <div class="bar" />
    </div>
  </div>
</template>

<script lang="ts">
import * as _ from "lodash-es";
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  toRefs,
  watch,
} from "vue";

import type { Event, FullTask } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseCheckbox from "@/components/base/BaseCheckbox.vue";
import { formatRelativeDate } from "@/components/dates";
import ActivityIcon from "@/components/icons/ActivityIcon.vue";
import NotesIcon from "@/components/icons/NotesIcon.vue";
import * as inject from "@/components/inject";
import { taskCheckboxTooltip } from "@/components/task/checkbox-tooltip";
import { completeRestoreTask } from "@/task";
import { navigateToObject } from "@/utils/navigate";

const INITIAL_EVENTS_LIMIT = 3;
const LOAD_MORE_INCREMENT = 10;

export default defineComponent({
  components: {
    ActivityIcon,
    BaseButton,
    BaseCheckbox,
    NotesIcon,
  },
  props: {
    events: { type: Array as PropType<Event[]>, required: true },
  },
  setup(props) {
    const { events } = toRefs(props);
    const controller = inject.controller();
    const router = inject.router();
    // Note: Vue 2 does not support async computed
    const eventTasks = ref<{ event: Event; tasks: FullTask[] }[] | null>(null);
    const eventsLimit = ref(INITIAL_EVENTS_LIMIT);

    const loadEventsTasks = (events: Event[]) => {
      eventTasks.value = [];
      events.forEach((event) => {
        controller.event.tasks(event.id).then((tasks: FullTask[]) => {
          eventTasks.value!.push({
            event,
            tasks: _.filter(tasks, (task) => !task.completed),
          });
        });
      });
    };
    onMounted(() => loadEventsTasks(events.value || []));
    watch(events, (events) => {
      eventsLimit.value = INITIAL_EVENTS_LIMIT;
      loadEventsTasks(events || []);
    });

    const displayedEvents = computed(() => {
      if (!eventTasks.value) {
        return [];
      }
      return eventTasks.value.slice(0, eventsLimit.value);
    });

    const hasMoreEvents = computed(() => {
      if (!eventTasks.value) {
        return false;
      }
      return eventTasks.value.length > eventsLimit.value;
    });

    const loadMore = () => {
      eventsLimit.value += LOAD_MORE_INCREMENT;
    };

    const openEvent = (id: string, mouseEvent?: MouseEvent) => {
      navigateToObject(
        router,
        controller,
        { kind: "event", id: id },
        mouseEvent
      );
    };
    const toggleCheckbox = (task: FullTask) =>
      completeRestoreTask(controller, task, "app");
    return {
      displayedEvents,
      eventTasks,
      formatRelativeDate,
      hasMoreEvents,
      loadMore,
      openEvent,
      taskCheckboxTooltip,
      toggleCheckbox,
    };
  },
});
</script>
<style scoped lang="scss">
.events {
  display: flex;
  flex-direction: row;
  cursor: default;
  column-gap: 16px;
  justify-content: space-between;

  .events-list {
    padding: 0;
    margin: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    row-gap: 2px;

    li:has(.base-button) {
      display: flex;
      margin-inline-start: 8px;
      margin-top: 4px;
    }

    .event {
      font-weight: normal;
      line-height: 19px;
      padding: 8px 16px;
      @include transition(background-color 100ms ease);

      &:hover {
        border-radius: $border-radius-m;
        @include themed() {
          background: t($grey-10);
        }

        .event-title {
          @include themed() {
            color: t($primary);
          }
        }
      }

      &:active {
        @include themed() {
          background: t($grey-15);
        }
      }

      p {
        max-width: 100%;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }

      .event-title {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin: 0;
        @extend %font-caption-regular;
        @include transition(color 100ms ease);
        @include themed() {
          color: t($grey-80);
        }

        p {
          margin: 0;
        }

        svg {
          flex-shrink: 0;
          margin-inline-start: 8px;
        }
      }

      .event-time {
        margin: 0;
        @extend %font-small-regular;
        @include themed() {
          color: t($grey-50);
        }
      }

      .uncompleted-tasks {
        padding: 8px 12px 0 14px;
        li {
          padding-bottom: 8px;
        }
      }
    }
  }
  .events-side {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    margin: 4px 0 0 0;

    .icon {
      flex-shrink: 0;
      @include themed() {
        color: t($grey-40);
      }
    }

    .bar {
      border: none;
      height: 100%;
      width: 1px;
      border-radius: 10px;
      margin: 12px 0 18px 0;
      @include themed() {
        background: t($grey-15);
      }
    }
  }
}
</style>
